import { enterprise, rebuild } from '@/axios/interface';
import { getCurrentInstance, ref } from 'vue';
import { resolvePageId, resolveStoreId } from '@rebuild/utils/context';

import LoadingMask from '@rebuild/components/loading/loading';
import { getLanguageType } from '@/locales';
import instance from '@/axios';
import { isErrorValue } from '@/utils/api';
import to from 'await-to-js';
import { useCommonData } from '@/hooks';
import { useStore } from '@/utils/vue-api';
import { MARKET_UNIT_MAP } from '@/store';

// v1_SZ000157 使用的本地 A/H 股票上下文。
export const SZ000157_MARKET_SOURCE = [
	{
		market: 'SZ',
		code: '000157',
		fullCode: 'sz000157',
		storeId: '20409',
		name: '中联重科',
		nameZhCht: '中联重科',
		nameEn: 'Zoomlion',
		unit: MARKET_UNIT_MAP.SZ
	},
	{
		market: 'HK',
		code: '01157',
		fullCode: 'hk01157',
		storeId: '29743',
		name: '中联重科',
		nameZhCht: '中联重科',
		nameEn: 'Zoomlion',
		unit: MARKET_UNIT_MAP.HK
	}
];

export function useFuncStep() {
	async function runSteps(steps) {
		let hasError = false;
		const results = [];

		for (const step of steps) {
			const result = await step();
			results.push(result);
			if (result.error) {
				hasError = true;
			}
		}

		return { hasError, results };
	}

	function wrapStep(fn) {
		return async () => {
			try {
				const data = await fn();
				// 如果函数 return 的结果是 falsy，也算失败
				if (data === undefined || data === null) {
					return { error: 'falsy result', data: null };
				}
				return { error: null, data };
			} catch (err) {
				return { error: err, data: null };
			}
		};
	}

	return {
		runSteps,
		wrapStep
	};
}

export function useCoreData() {
	const vm = getCurrentInstance();
	const store = useStore();

	const responseData = res => res.data?.data;

	const { viewInIframe, viewInPreview, multiTemplates } = useCommonData();

	// ===================== 加载状态 =====================
	const isLoading = ref(true);
	const showLoading = () => {
		isLoading.value = true;
		LoadingMask.show();
	};
	const hideLoading = () => {
		LoadingMask.hide();
		isLoading.value = false;
	};

	// ===================== 基础数据 =====================
	const loadIRBasicInfo = async () => {
		const storeId = resolveStoreId();
		if (isErrorValue('storeId', storeId)) return;

		const [err, res] = await to(instance.get(enterprise.getCommonIRStore(storeId)));
		if (err) return;

		const data = responseData(res) || {};
		store.commit('setColumnData', data);
		return data;
	};
	const loadIRPageData = async () => {
		const storeId = resolveStoreId();
		const pageId = resolvePageId();
		if (isErrorValue('storeId', storeId) || isErrorValue('pageId', pageId)) return;

		const usePreviewApi = viewInIframe.value || viewInPreview.value;
		const queryApi = usePreviewApi
			? enterprise.getPageSaveContent(storeId, pageId)
			: enterprise.getPageDetail(storeId, pageId);
		const [err, res] = await to(instance.get(queryApi));
		if (err) return;

		const data = responseData(res) || {};
		await updateMarketSource(data);
		setTemplateData(data);
		return data;
	};
	const setTemplateData = data => {
		store.commit('setCommonData', {
			data: data,
			overwrite: true
		});
	};
	const updateMarketSource = async data => {
		// 多地上市
		return instance
			.get(enterprise.getManyStock(data.fullCode))
			.then(res => {
				const marketList = res.data?.data || [];
				const listToObj = marketList.reduce((acc, item) => {
					const fullCode = `${item.market}${item.code}`;
					acc[fullCode] = {
						unit: MARKET_UNIT_MAP[item.market.toUpperCase()],
						market: item.market,
						code: item.code,
						fullCode: fullCode
					};
					return acc;
				}, {});
				store.commit('setMarketSource', listToObj);
			})
			.catch(() => {
				console.error('no stock data:', data.fullCode);
			});
	};

	const queryPageList = async tmplId => {
		if (!tmplId) return null;

		const storeId = resolveStoreId();
		if (isErrorValue('storeId', storeId)) return null;

		const [err, res] = await to(instance.post(rebuild.queryPageListWithTemplateId(storeId, tmplId)));
		if (err) return null;

		return responseData(res);
	};

	// ===================== 模板配置表 =====================
	// 通过模板ID查配置表
	const loadUIConfigWithTmplId = async () => {
		const currTemplate = store.getters.getIrPageType;

		let tmplId = null,
			pageId = null,
			pageList = null;

		if (multiTemplates.includes(currTemplate)) {
			// 新模板，直接取链接上模板ID
			tmplId = vm.proxy.$route.params?.tmplId;
			pageList = await queryPageList(tmplId);
			pageId = findPageIdWithCurrLanguage(pageList);
		} else {
			// 历史模板，先取页面ID，再查模板ID
			const { storePageInfoList = [] } = store.getters.getBaseData;
			pageList = storePageInfoList.filter(v => v.pageType === currTemplate);
			pageId = findPageIdWithCurrLanguage(pageList);
			tmplId = await getTemplateIdWithPageId(pageId);
		}

		store.commit('setTemplateId', tmplId);
		store.commit('setPageId', pageId);
		store.commit('setPageList', pageList);

		const config = await getTemplateConfig(tmplId);
		store.commit('setUiConfig', config || {});
		return config;
	};
	const findPageIdWithCurrLanguage = (pageList = []) => {
		const currLanguageType = getLanguageType();
		const findPageInfo = pageList.find(v => v.languageType === currLanguageType);
		return findPageInfo && findPageInfo.pageId;
	};
	const getTemplateIdWithPageId = async pageId => {
		if (!pageId) return null;

		const storeId = resolveStoreId();
		if (isErrorValue('storeId', storeId)) return null;

		const [err, res] = await to(instance.get(rebuild.queryModuleIdWithPageId(storeId, pageId)));
		if (err) return null;

		return responseData(res);
	};
	const getTemplateConfig = async tmplId => {
		const storeId = resolveStoreId();
		if (isErrorValue('storeId', storeId) || !tmplId) return;

		const [err, res] = await to(
			instance.get(rebuild.queryTemplateConfig(storeId, tmplId), {
				languageType: getLanguageType()
			})
		);

		if (err) return;

		const data = responseData(res) || {};
		const { styleVOList = [], moduleConfigVOList = [] } = data;

		const modulesConfig = moduleConfigVOList.map(v => ({
			...v,
			id: v.moduleId
		}));

		const config = {
			layout: styleVOList,
			modules: modulesConfig
		};
		return applyDevVersionOverride(config);
	};

	return {
		isLoading,
		showLoading,
		hideLoading,

		loadIRBasicInfo,
		loadIRPageData,
		loadUIConfigWithTmplId,
		setTemplateData
	};
}

// 本地验证定制版本
const applyDevVersionOverride = config => {
	if (!config || process.env.NODE_ENV === 'production') return config;
	const devVersion = new URLSearchParams(location.search).get('devVersion');
	if (!devVersion) return config;
	console.warn(`[devVersion] 版本覆盖已激活：${devVersion}`);
	const override = arr => arr.map(v => ({ ...v, version: devVersion }));
	return {
		layout: override(config.layout || []),
		modules: override(config.modules || [])
	};
};
